import { HStack, Text } from '@chakra-ui/react'

import { CircleLogo } from '@/components/molecules/User/Circle/CircleLogo'
import { Avatar } from '@/components/ui/avatar'
import { useMyCirclesMap } from '@/hooks/useMyCirclesMap'
import { isContextualScope } from '@/utils/notificationScope'

interface NotificationScopeBadgeProps {
  scopeType?: string | null
  scopeIri?: string | null
  scopeName?: string | null
}

/**
 * Contextual badge shown on a notification received as a community/network admin: the circle
 * logo (resolved from the user's circles, shared cache with the ContextSwitcher) + name.
 * Renders nothing for the default personal (user) scope.
 */
export function NotificationScopeBadge({
  scopeType,
  scopeIri,
  scopeName,
}: NotificationScopeBadgeProps) {
  const circlesById = useMyCirclesMap(scopeType !== 'circle')

  if (!isContextualScope({ scopeType, scopeIri, scopeName })) {
    return null
  }

  const circle =
    scopeType === 'circle' && scopeIri ? circlesById.get(scopeIri) : undefined
  const label = scopeName ?? circle?.name ?? ''

  return (
    <HStack
      gap={1}
      bg="blackAlpha.50"
      _dark={{ bg: 'whiteAlpha.100' }}
      borderRadius="full"
      pl="0.5"
      pr="2"
      py="0.5"
      minW={0}
      maxW="100%"
      flexShrink={1}
    >
      {circle ? (
        <CircleLogo circle={circle} size="2xs" />
      ) : (
        <Avatar size="2xs" name={label} />
      )}
      <Text fontSize="xs" fontWeight="medium" lineClamp={1}>
        {label}
      </Text>
    </HStack>
  )
}
